import React from 'react';
import ReactDOM from 'react-dom';
import styles from './Modal.module.css';

export type ModalProps = {
  open: boolean;
  title?: React.ReactNode;
  /** Optional `data-testid` to apply to the title <h2>. */
  titleTestId?: string;
  /** Optional `data-testid` to apply to the "X" close button. */
  closeButtonTestId?: string;
  onClose: () => void;
  footer?: React.ReactNode;
  children: React.ReactNode;
  contentRef?: React.Ref<HTMLDivElement>;
  width?: number | string;
  height?: number | string;
};

export const Modal: React.FC<ModalProps> = ({ open, title, titleTestId, closeButtonTestId, onClose, footer, children, contentRef, width, height }) => {
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const toCssSize = (v?: number | string, dimension?: 'width' | 'height') => {
    if (typeof v === 'number') {
      // Use CSS min() to clamp to 95% of viewport so it adapts on orientation change
      if (dimension) {
        const unit = dimension === 'width' ? 'vw' : 'vh';
        return `min(${v}px, 95${unit})`;
      }
      return `${v}px`;
    }
    return v;
  };

  // Expose sizing as CSS variables so CSS can adapt per media queries
  const containerStyle: React.CSSProperties = {
    ['--modal-max-width']: toCssSize(width, 'width'),
    ['--modal-max-height']: toCssSize(height, 'height'),
  };

  return ReactDOM.createPortal(
    <div
      className={[
        styles.overlay,
        !open ? styles.overlayHidden : ''
      ].join(' ').trim()}
      onClick={handleOverlayClick}
    >
      <div
        className={styles.container}
        style={containerStyle}
      >
        {title && (
          <div className={styles.header}>
            <h2 className={styles.title} data-testid={titleTestId}>{title}</h2>
            <button
              className={styles.close}
              onClick={onClose}
              aria-label="Close"
              data-testid={closeButtonTestId}
            >
              <svg width="21.6" height="21.6" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M0.509091 0.827027C-0.169697 1.50811 -0.169697 2.62701 0.509091 3.3081L7.78182 10.6054C7.87879 10.7027 7.87879 10.8972 7.78182 10.9945L0.654547 18.1459C-0.121211 18.9243 -0.121211 20.1892 0.654547 21.0162C1.4303 21.7946 2.69091 21.7946 3.51515 21.0162L10.6424 13.8648C10.7394 13.7675 10.9333 13.7675 11.0303 13.8648L18.1576 21.0162C18.9333 21.7946 20.1939 21.7946 21.0182 21.0162C21.7939 20.2378 21.7939 18.973 21.0182 18.1459L13.8909 10.9945C13.7939 10.8972 13.7939 10.7027 13.8909 10.6054L21.0182 3.45401C21.7939 2.67563 21.7939 1.41081 21.0182 0.583784C20.2424 -0.194595 18.9818 -0.194595 18.1576 0.583784L11.0303 7.73511C10.9333 7.83241 10.7394 7.83241 10.6424 7.73511L3.56364 0.632421C2.78788 -0.145958 1.52727 -0.145958 0.703031 0.632421L0.509091 0.827027Z" fill="#CF001C"/>
              </svg>
            </button>
          </div>
        )}
        <div className={styles.content} ref={contentRef}>{children}</div>
        {footer}
      </div>
    </div>,
    document.body
  );
};
